import type { AccountInfo } from '@azure/msal-browser'

/**
 * Typed view of the JWT claims Murmuration cares about.
 *
 * MSAL Browser parses the id_token claims into `account.idTokenClaims` (an
 * untyped `Record<string, unknown>`). We narrow that here so the route
 * guard and `useAuth()` hook can rely on a stable shape. Unknown / missing
 * fields are returned as `undefined`; the guard interprets `undefined` as
 * "not authorised" rather than "transient", matching the M1 fail-closed
 * posture.
 *
 * Field provenance (per `07a-security-design.md` Section 3a and ADR-008):
 *   - `sub`     - Entra `oid` (durable per-user identifier). MSAL also
 *                 exposes this via `account.localAccountId`.
 *   - `tnt`     - Custom optional claim mapped from the Murmuration app-role
 *                 (see `07a` OAQ-1 verdict: app-role-based claim).
 *   - `roles`   - Entra app roles. The role for staff is `bo.staff` per
 *                 `07a` E3 / OAQ-8.
 *   - `name`    - Display name. Prefer this for the staff identity menu.
 *   - `preferred_username` - UPN-style fallback when `name` is empty.
 *   - `aud`/`iss`/`exp` - standard. Verified by the APIGW JWT authoriser;
 *                 the SPA inspects them for diagnostics only.
 */
export interface MurmurationClaims {
  sub: string | undefined
  tnt: string | undefined
  roles: readonly string[]
  name: string | undefined
  preferred_username: string | undefined
  aud: string | undefined
  iss: string | undefined
  exp: number | undefined
}

/** Tenant we accept at M1. Defence-in-depth on top of the APIGW authoriser. */
export const REQUIRED_TENANT = 'suitsme'

/** Staff role we require on the Entra app registration. Per `07a` OAQ-1 verdict. */
export const REQUIRED_ROLE = 'bo.staff'

/**
 * Roles that make someone Murmuration staff rather than credit union staff.
 *
 * Named on the MUR page "Information Architecture and Navigation": the
 * `Platform` nav group "is rendered only when the signed-in user holds a
 * Murmuration role: Murmuration support, Platform administrator, or Super
 * administrator. A credit union user must never see the group label, let
 * alone a route."
 *
 * Two things about this list that a reader needs to know.
 *
 * First, **no Entra token issues any of these today.** The app registration
 * grants `bo.staff` and nothing else, so `isMurmurationStaff` returns false
 * for every real session and the Platform group renders for nobody. That is
 * the correct fail-closed default, not an oversight - the alternative is
 * defaulting a control-plane group open.
 *
 * Second, the identifiers below are a **provisional mapping**. The MUR page
 * names these roles in prose; it does not publish their claim values, and
 * open question Q3 records that the role set does not currently agree with
 * itself (Roles and Permissions lists 16 roles, the Master Feature List says
 * 17, and Board member and Auditor appear in the Treasury permissions matrix
 * but not the platform role set). Q3 is owned by `business-architect`.
 * Reconcile there, then correct these values - do not settle the role set
 * here.
 */
export const MURMURATION_ROLES: readonly string[] = [
  'mur.support',
  'mur.platform_admin',
  'mur.super_admin',
]

/**
 * Whether the signed-in user is Murmuration staff, and so may see the
 * `Platform` nav group and its routes.
 *
 * **This is presentation, not the control.** The MUR IA page is explicit:
 * "Gating is server-side; hiding the nav entry is presentation, not the
 * control." Hiding a nav entry stops a credit union user stumbling into a
 * control-plane screen; it does not stop anyone who types the URL. The
 * server must reject those routes independently, and this function must
 * never be the only thing standing between a tenant and the control plane.
 *
 * Returns false for a null claim set, an absent `roles` array, and any role
 * outside {@link MURMURATION_ROLES}.
 */
export function isMurmurationStaff(claims: MurmurationClaims | null): boolean {
  if (!claims) return false
  return claims.roles.some((role) => MURMURATION_ROLES.includes(role))
}

/**
 * Extract the typed claim view from an MSAL `AccountInfo`. Returns `null`
 * when there is no signed-in account, so callers can branch cleanly.
 */
export function extractClaims(account: AccountInfo | null | undefined): MurmurationClaims | null {
  if (!account) {
    return null
  }
  const raw = (account.idTokenClaims ?? {}) as Record<string, unknown>

  return {
    sub: stringClaim(raw, 'sub') ?? nonEmpty(account.localAccountId),
    tnt: stringClaim(raw, 'tnt'),
    roles: stringArrayClaim(raw, 'roles'),
    name: stringClaim(raw, 'name') ?? nonEmpty(account.name),
    preferred_username: stringClaim(raw, 'preferred_username') ?? nonEmpty(account.username),
    aud: stringClaim(raw, 'aud'),
    iss: stringClaim(raw, 'iss'),
    exp: numberClaim(raw, 'exp'),
  }
}

/**
 * Display name to surface in the staff identity menu. Falls back through
 * the chain `name -> preferred_username -> sub -> 'Unknown user'` so the
 * UI never renders an empty string.
 */
export function displayName(claims: MurmurationClaims | null): string {
  if (!claims) return 'Unknown user'
  return claims.name ?? claims.preferred_username ?? claims.sub ?? 'Unknown user'
}

/** Options for {@link isAuthorised}. */
export interface AuthorisationOptions {
  /**
   * Skip the `tnt` (tenant) comparison. Intended ONLY for the non-prod dev
   * escape hatch wired in `dev/flags.ts` (`VITE_SKIP_TENANT_CHECK`), e.g.
   * when a real Entra token does not yet carry the `tnt` optional claim.
   * The role check is unaffected. Defaults to `false`, so production and
   * tests keep the full fail-closed behaviour.
   */
  readonly skipTenantCheck?: boolean
}

/**
 * Whether the staff token carries the M1 tenant + role. Both checks are
 * required; the guard renders `/access-denied` if either fails. The tenant
 * check can be skipped via {@link AuthorisationOptions.skipTenantCheck} for
 * the dev-only bypass; the role check is always enforced.
 */
export function isAuthorised(
  claims: MurmurationClaims | null,
  opts: AuthorisationOptions = {},
): boolean {
  if (!claims) return false
  if (!opts.skipTenantCheck && claims.tnt !== REQUIRED_TENANT) return false
  if (!claims.roles.includes(REQUIRED_ROLE)) return false
  return true
}

function stringClaim(raw: Record<string, unknown>, key: string): string | undefined {
  const v = raw[key]
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

function nonEmpty(v: string | undefined): string | undefined {
  if (typeof v !== 'string') return undefined
  return v.length > 0 ? v : undefined
}

function numberClaim(raw: Record<string, unknown>, key: string): number | undefined {
  const v = raw[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function stringArrayClaim(raw: Record<string, unknown>, key: string): readonly string[] {
  const v = raw[key]
  if (!Array.isArray(v)) return []
  return v.filter((x): x is string => typeof x === 'string')
}
